// Real data structure for Pediatric Health Assessment based on CSV file

// Survey Automation Rules
export const surveyAutomationRules = [
  // Language Selection (applies to all)
  {
    id: "lang_selection",
    questionId: "all",
    condition: "Survey Start",
    triggerCondition: "Survey initialization",
    actions: [
      {
        type: "resource",
        resource: "Language Selection",
        schedule: "immediate",
        description: "Select Patient Preferred Language and Send Spanish or English Assessment and Resource based on preferred language",
        technicalKey: "select_language"
      }
    ]
  },
  
  // Q29.1 - Mental Health Resources
  {
    id: "mental_health_resources",
    questionId: "q29.1", 
    condition: "[29.1] = A",
    triggerCondition: "Wants help with emotional well-being",
    actions: [
      {
        type: "resource",
        resource: "Beating the worry bug",
        schedule: "+7 days",
        description: "Send mental health resource",
        technicalKey: "send_worry_bug_resource"
      },
      {
        type: "resource", 
        resource: "Mindfulness Introduction",
        schedule: "+8 days",
        description: "Send 2nd resource",
        technicalKey: "send_mindfulness_introduction_resource",
        notes: "Has 2 reminders (6h & 12h later)"
      },
      {
        type: "resource",
        resource: "Mindfulness Exercise", 
        schedule: "+9 days",
        description: "Send 3rd resource",
        technicalKey: "send_mindfulness_meditation_resource",
        notes: "Also has 2 reminders (6h & 12h later), +1 day after intro engaged"
      }
    ]
  },

  // Q29.2 - Allergy Test Resource
  {
    id: "allergy_test_resource",
    questionId: "q29.2",
    condition: "[29.2] = A", 
    triggerCondition: "Wants allergy test",
    actions: [
      {
        type: "resource",
        resource: "Allergy Video",
        schedule: "immediate",
        description: "Send Resource 1 immediately",
        technicalKey: "send_allergy_resource"
      }
    ]
  },

  // Q29.2 OR Q29.3 - Immunotherapy Resource
  {
    id: "immunotherapy_resource",
    questionId: ["q29.2", "q29.3"],
    condition: "[29.2] = A OR [29.3] = A",
    triggerCondition: "Wants allergy test OR alternative treatment",
    actions: [
      {
        type: "resource", 
        resource: "Allergen Immunotherapy (Allergy Shots)",
        schedule: "+7 days",
        description: "Send immunotherapy resource",
        technicalKey: "send_allergen_immunotherapy_resource"
      }
    ]
  },

  // Q29.4 - Wellness Resources
  {
    id: "wellness_resources",
    questionId: "q29.4",
    condition: "[29.4] = A",
    triggerCondition: "Wants help with relaxation, weight loss, healthy eating, or exercise",
    actions: [
      {
        type: "resource",
        resource: "Progressive Muscle Relaxation introduction", 
        schedule: "+7 days",
        description: "Send wellness resource",
        technicalKey: "send_progressive_introduction_resource"
      },
      {
        type: "resource",
        resource: "Progressive Muscle Relaxation Exercise",
        schedule: "+8 days", 
        description: "Send 2nd resource",
        technicalKey: "send_progressive_resource"
      },
      {
        type: "resource",
        resource: "Eatwell",
        schedule: "+14 days",
        description: "Send 3rd resource", 
        technicalKey: "send_eat_well_resource"
      },
      {
        type: "resource",
        resource: "SmartFood",
        schedule: "+21 days",
        description: "Send 4th resource",
        technicalKey: "send_smart_food_resource"
      }
    ]
  }
];

export const pediatricHealthAssessmentData = {
  // Intro Card (appears before questions)
  introCard: {
    type: "intro_card",
    title: "Pediatric Health Assessment Introduction",
    text: "This Health Assessment asks questions about general health and well-being, including potential behavioral and developmental concerns, allergy and asthma symptoms, sleep, and quality-of-life issues that might be affecting your child. The answers will help your healthcare provider better understand your child's needs and tailor care appropriately.\n\nWhen answering these questions, please remember:\n\n• \"You\" refers to the child or teen whose health is being assessed.\n• If you are a parent or caregiver, please answer on your child's behalf—unless your child is old enough (typically age 12 or above) to answer for themselves.\n• Feel free to let your child respond directly if they are able and willing to do so with your supervision."
  },

  // Survey metadata
  title: "Pediatric Health Assessment",
  version: "7.4.25",
  totalQuestions: 29,
  estimatedTime: "10-15 minutes",
  
  // Survey automation rules
  surveyAutomationRules: surveyAutomationRules,
  
  questions: [
    {
      id: "q0",
      number: 0,
      text: "Age today",
      type: "pull_from_db",
      fieldName: "age_today",
      required: true,
      options: [
        { value: "A", label: ">=4", numericValue: 0 },
        { value: "B", label: ">=8", numericValue: null },
        { value: "C", label: ">=12", numericValue: null }
      ],
      automation: null,
      notes: "Age-based logic for survey questions"
    },

    // Group header for PSC-17
    {
      id: "q1_group",
      number: 1,
      text: "Pediatric Symptom Checklist 17",
      type: "group",
      fieldName: "psc_17_group",
      required: true,
      sectionText: "[FirstName], your answers help us understand how you or your child is doing day today.",
      estimatedTime: "1-2 mins",
      automation: null
    },

    // PSC-17 Grid Question
    {
      id: "q1_grid",
      number: 1,
      text: "Please select the answer that best describes these symptoms, whether they apply to you or your child.",
      type: "grid",
      fieldName: "psc_17_grid",
      required: true,
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    // Individual PSC-17 questions
    {
      id: "q1.1",
      number: "1.1",
      text: "Fidgety, unable to sit still",
      type: "grid_item",
      fieldName: "psc_1_1",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.2",
      number: "1.2",
      text: "Feels sad, unhappy",
      type: "grid_item",
      fieldName: "psc_1_2",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.3",
      number: "1.3",
      text: "Daydreams too much",
      type: "grid_item",
      fieldName: "psc_1_3",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.4",
      number: "1.4",
      text: "Refuses to share",
      type: "grid_item",
      fieldName: "psc_1_4",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.5",
      number: "1.5",
      text: "Does not understand other people's feelings",
      type: "grid_item",
      fieldName: "psc_1_5",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.6",
      number: "1.6",
      text: "Feels hopeless",
      type: "grid_item",
      fieldName: "psc_1_6",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.7",
      number: "1.7",
      text: "Has trouble concentrating",
      type: "grid_item",
      fieldName: "psc_1_7",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.8",
      number: "1.8",
      text: "Fights with other children",
      type: "grid_item",
      fieldName: "psc_1_8",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.9",
      number: "1.9",
      text: "Is down on him or herself",
      type: "grid_item",
      fieldName: "psc_1_9",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.10",
      number: "1.10",
      text: "Blames others for his or her troubles",
      type: "grid_item",
      fieldName: "psc_1_10",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.11",
      number: "1.11",
      text: "Seems to be having less fun",
      type: "grid_item",
      fieldName: "psc_1_11",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.12",
      number: "1.12",
      text: "Does not listen to rules",
      type: "grid_item",
      fieldName: "psc_1_12",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.13",
      number: "1.13",
      text: "Acts as if driven by a motor",
      type: "grid_item",
      fieldName: "psc_1_13",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.14",
      number: "1.14",
      text: "Teases others",
      type: "grid_item",
      fieldName: "psc_1_14",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.15",
      number: "1.15",
      text: "Worries a lot",
      type: "grid_item",
      fieldName: "psc_1_15",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.16",
      number: "1.16",
      text: "Takes things that do not belong to him or her",
      type: "grid_item",
      fieldName: "psc_1_16",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q1.17",
      number: "1.17",
      text: "Distracted easily",
      type: "grid_item",
      fieldName: "psc_1_17",
      required: true,
      parentGrid: "q1_grid",
      options: [
        { value: "A", label: "Never", numericValue: 0 },
        { value: "B", label: "Sometimes", numericValue: 1 },
        { value: "C", label: "Often", numericValue: 2 }
      ],
      automation: null
    },

    // SDQ Group
    {
      id: "q2_group",
      number: 2,
      text: "Strengths and Difficulties Questionnaire",
      type: "group",
      fieldName: "sdq_group",
      required: true,
      sectionText: "Thank you — every response shapes better support for yours or your child's strengths.",
      estimatedTime: "<1 mins",
      automation: null
    },

    // SDQ First Grid
    {
      id: "q2_grid",
      number: 2,
      text: "What best describes these behaviors? (Answer for yourself, or about your child if you are a parent or carer.)",
      type: "grid",
      fieldName: "sdq_grid_1",
      required: true,
      options: [
        { value: "A", label: "Not True", numericValue: 2 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 0 }
      ],
      automation: null
    },

    // SDQ Grid Items 2.1-2.3
    {
      id: "q2.1",
      number: "2.1",
      text: "Considerate of other people's feelings",
      type: "grid_item",
      fieldName: "sdq_2_1",
      required: true,
      parentGrid: "q2_grid",
      options: [
        { value: "A", label: "Not True", numericValue: 2 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q2.2",
      number: "2.2",
      text: "Has at least one good friend",
      type: "grid_item",
      fieldName: "sdq_2_2",
      required: true,
      parentGrid: "q2_grid",
      options: [
        { value: "A", label: "Not True", numericValue: 2 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q2.3",
      number: "2.3",
      text: "Generally well-behaved, does what adults request",
      type: "grid_item",
      fieldName: "sdq_2_3",
      required: true,
      parentGrid: "q2_grid",
      options: [
        { value: "A", label: "Not True", numericValue: 2 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 0 }
      ],
      automation: null
    },

    // SDQ Second Grid
    {
      id: "q3",
      number: 3,
      text: "What best describes these behaviors? (Answer for yourself, or about your child if you are a parent or carer.)",
      type: "grid",
      fieldName: "sdq_grid_2",
      required: true,
      options: [
        { value: "A", label: "Not True", numericValue: 0 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 2 }
      ],
      automation: null
    },

    // SDQ Grid Items 3.1-3.2
    {
      id: "q3.1",
      number: "3.1",
      text: "Restless, overactive, cannot stay still for long",
      type: "grid_item",
      fieldName: "sdq_3_1",
      required: true,
      parentGrid: "q3",
      options: [
        { value: "A", label: "Not True", numericValue: 0 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q3.2",
      number: "3.2",
      text: "Often complains of headaches/stomach-aches/sickness",
      type: "grid_item",
      fieldName: "sdq_3_2",
      required: true,
      parentGrid: "q3",
      options: [
        { value: "A", label: "Not True", numericValue: 0 },
        { value: "B", label: "Somewhat True", numericValue: 1 },
        { value: "C", label: "Certainly True", numericValue: 2 }
      ],
      automation: null
    },

    {
      id: "q4",
      number: 4,
      text: "In the past month, have you (or has your child) had serious thoughts about ending life?",
      type: "radio",
      fieldName: "suicidal_thoughts",
      required: true,
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q5",
      number: 5,
      text: "Have you (or has your child) ever tried to kill themselves or made a suicide attempt?",
      type: "radio",
      fieldName: "suicide_attempt",
      required: true,
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q6",
      number: 6,
      text: "Nasal, Sinus, & Eye Allergy Symptoms",
      type: "group",
      fieldName: "nasal_symptoms_group",
      required: true,
      sectionText: "Did you know? A single sneeze can travel ≈ 100 mph!",
      estimatedTime: "<1 min",
      automation: null
    },

    {
      id: "q7",
      number: 7,
      text: "In the past few months, have you (or your child) had any of these nose or sinus symptoms?",
      type: "checkbox",
      fieldName: "nasal_symptoms",
      required: false,
      subText: "If you do not have any of these symptoms go to the next question.",
      options: [
        { value: "A", label: "Runny or stuffy nose", numericValue: 1 },
        { value: "B", label: "Sneezing", numericValue: 2 },
        { value: "C", label: "Itchy nose", numericValue: 3 },
        { value: "D", label: "Blocked nose", numericValue: 4 },
        { value: "E", label: "Post nasal drip / drainage", numericValue: 5 },
        { value: "F", label: "Mouth breathing / snoring", numericValue: 6 },
        { value: "G", label: "Frequent sniffling", numericValue: 7 },
        { value: "H", label: "Decreased smell", numericValue: 8 },
        { value: "I", label: "Headaches", numericValue: 9 },
        { value: "J", label: "Facial pressure / pain", numericValue: 10 }
      ],
      automation: {
        type: "conditional_display",
        rules: [
          {
            condition: "[7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J",
            action: "enable",
            targetQuestions: ["q8", "q9", "q10"],
            description: "Enable seasonal and timing questions if any nasal symptoms selected"
          }
        ]
      }
    },

    {
      id: "q8",
      number: 8,
      text: "Do you have these nasal or sinus symptoms:",
      type: "checkbox",
      fieldName: "nasal_symptoms_timing",
      required: false,
      showIf: "[7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J",
      options: [
        { value: "A", label: "All year round", numericValue: 1 },
        { value: "B", label: "Spring", numericValue: 2 },
        { value: "C", label: "Summer", numericValue: 3 },
        { value: "D", label: "Fall", numericValue: 4 },
        { value: "E", label: "Winter", numericValue: 5 },
        { value: "F", label: "They just started", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q9",
      number: 9,
      text: "Are your nasal or sinus symptoms worse:",
      type: "checkbox",
      fieldName: "nasal_symptoms_worse",
      required: false,
      showIf: "[7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J",
      options: [
        { value: "A", label: "At night", numericValue: 1 },
        { value: "B", label: "During the day", numericValue: 2 },
        { value: "C", label: "Both night and day", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q10",
      number: 10,
      text: "Do your nasal or sinus symptoms mostly occur:",
      type: "checkbox",
      fieldName: "nasal_symptoms_location",
      required: false,
      showIf: "[7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J",
      options: [
        { value: "A", label: "Indoor", numericValue: 1 },
        { value: "B", label: "Outdoor", numericValue: 2 },
        { value: "C", label: "Both indoor and outdoor", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q11",
      number: 11,
      text: "In the past few months, have you (or your child) had any of these eye symptoms?",
      type: "checkbox",
      fieldName: "eye_symptoms",
      required: false,
      subText: "If you do not have any of these symptoms go to the next question.",
      options: [
        { value: "A", label: "Red eyes", numericValue: 1 },
        { value: "B", label: "Itchy eyes", numericValue: 2 },
        { value: "C", label: "Watery eyes", numericValue: 3 },
        { value: "D", label: "Dark circles", numericValue: 4 },
        { value: "E", label: "Swollen dry eyes", numericValue: 5 },
        { value: "F", label: "Sore eyes", numericValue: 6 }
      ],
      automation: {
        type: "conditional_display",
        rules: [
          {
            condition: "[11] = A OR B OR C OR D OR E OR F",
            action: "enable",
            targetQuestions: ["q12", "q13", "q14"],
            description: "Enable eye symptom timing questions if any eye symptoms selected"
          }
        ]
      }
    },

    {
      id: "q12",
      number: 12,
      text: "Do you have these eye symptoms:",
      type: "checkbox",
      fieldName: "eye_symptoms_timing",
      required: false,
      showIf: "[11] = A OR B OR C OR D OR E OR F",
      options: [
        { value: "A", label: "All year round", numericValue: 1 },
        { value: "B", label: "Spring", numericValue: 2 },
        { value: "C", label: "Summer", numericValue: 3 },
        { value: "D", label: "Fall", numericValue: 4 },
        { value: "E", label: "Winter", numericValue: 5 },
        { value: "F", label: "They just started", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q13",
      number: 13,
      text: "Are your eye symptoms worse:",
      type: "checkbox",
      fieldName: "eye_symptoms_worse",
      required: false,
      showIf: "[11] = A OR B OR C OR D OR E OR F",
      options: [
        { value: "A", label: "At night", numericValue: 1 },
        { value: "B", label: "During the day", numericValue: 2 },
        { value: "C", label: "Both night and day", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q14",
      number: 14,
      text: "Do your eye symptoms mostly occur:",
      type: "checkbox",
      fieldName: "eye_symptoms_location",
      required: false,
      showIf: "[11] = A OR B OR C OR D OR E OR F",
      options: [
        { value: "A", label: "Indoor", numericValue: 1 },
        { value: "B", label: "Outdoor", numericValue: 2 },
        { value: "C", label: "Both indoor and outdoor", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q15",
      number: 15,
      text: "Do you have or have you ever been told that you have Asthma?",
      type: "radio",
      fieldName: "has_asthma",
      required: true,
      options: [
        { value: "A", label: "Yes", numericValue: null },
        { value: "B", label: "No", numericValue: null },
        { value: "C", label: "Unsure", numericValue: null }
      ],
      automation: {
        type: "conditional_display",
        rules: [
          {
            condition: "[15] = A",
            action: "enable",
            targetQuestions: ["q21"],
            description: "Enable allergy control questions if has asthma"
          }
        ]
      }
    },

    // Chest Allergy Symptoms Group
    {
      id: "q16",
      number: 16,
      text: "Chest Allergy Symptoms",
      type: "group",
      fieldName: "chest_symptoms_group",
      required: true,
      sectionText: "Did you know? Kids breathe 20-30 times a minute—about twice an adult's rate.",
      estimatedTime: "30 seconds",
      automation: null
    },

    {
      id: "q17",
      number: 17,
      text: "In the past few months, have you (or your child) had any of these chest symptoms?",
      type: "checkbox",
      fieldName: "chest_symptoms",
      required: false,
      subText: "If you do not have any of these symptoms go to the next question.",
      options: [
        { value: "A", label: "Wheezing", numericValue: 1 },
        { value: "B", label: "Coughing", numericValue: 2 },
        { value: "C", label: "Tightness", numericValue: 3 },
        { value: "D", label: "Shortness of Breath", numericValue: 4 },
        { value: "E", label: "Chronic Bronchitis", numericValue: 5 }
      ],
      automation: {
        type: "conditional_display",
        rules: [
          {
            condition: "[17] = A OR B OR C OR D OR E",
            action: "enable",
            targetQuestions: ["q18", "q19", "q20"],
            description: "Enable chest symptom timing questions if any chest symptoms selected"
          }
        ]
      }
    },

    {
      id: "q18",
      number: 18,
      text: "Do you have these chest symptoms:",
      type: "checkbox",
      fieldName: "chest_symptoms_timing",
      required: false,
      options: [
        { value: "A", label: "All year round", numericValue: 1 },
        { value: "B", label: "Spring", numericValue: 2 },
        { value: "C", label: "Summer", numericValue: 3 },
        { value: "D", label: "Fall", numericValue: 4 },
        { value: "E", label: "Winter", numericValue: 5 },
        { value: "F", label: "They just started", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q19",
      number: 19,
      text: "Are your chest symptoms worse:",
      type: "checkbox",
      fieldName: "chest_symptoms_worse",
      required: false,
      options: [
        { value: "A", label: "At night", numericValue: 1 },
        { value: "B", label: "During the day", numericValue: 2 },
        { value: "C", label: "Both night and day", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q20",
      number: 20,
      text: "Do your chest symptoms mostly occur:",
      type: "checkbox",
      fieldName: "chest_symptoms_location",
      required: false,
      options: [
        { value: "A", label: "Indoor", numericValue: 1 },
        { value: "B", label: "Outdoor", numericValue: 2 },
        { value: "C", label: "Both indoor and outdoor", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q21",
      number: 21,
      text: "Control of Allergy and Asthma Symptoms",
      type: "group",
      fieldName: "carat_score_group",
      required: true,
      showIf: "([7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J) OR ([11] = A OR B OR C OR D OR E OR F) OR ([17] = A OR B OR C OR D OR E ) OR [15] = A",
      sectionText: "Keep going — these questions help us fine-tune allergy care.",
      estimatedTime: "<1 min",
      automation: {
        type: "calculation",
        formula: "SUM [21.1,21.2, 21.3,21.4,21.5, 21.6,21.7,21.8,21.9,22] = CARAT SCORE",
        description: "Calculate CARAT (Control of Allergic Rhinitis and Asthma Test) score"
      }
    },

    {
      id: "q21.0",
      number: 21,
      text: "Because of asthma, allergies, or breathing trouble in the last four weeks, about how many times have you (or your child) had:",
      type: "grid",
      fieldName: "carat_grid",
      required: true,
      parentGrid: "q21",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.1",
      number: "21.1",
      text: "Blocked nose?",
      type: "grid_item",
      fieldName: "carat_blocked_nose",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.2",
      number: "21.2",
      text: "Sneezing?",
      type: "grid_item",
      fieldName: "carat_sneezing",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.3",
      number: "21.3",
      text: "Itchy nose?",
      type: "grid_item",
      fieldName: "carat_itchy_nose",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.4",
      number: "21.4",
      text: "Runny nose?",
      type: "grid_item",
      fieldName: "carat_runny_nose",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.5",
      number: "21.5",
      text: "Shortness of breath/dyspnea?",
      type: "grid_item",
      fieldName: "carat_shortness_breath",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.6",
      number: "21.6",
      text: "Wheezing in the chest?",
      type: "grid_item",
      fieldName: "carat_wheezing",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.7",
      number: "21.7",
      text: "Chest tightness upon physical exercise?",
      type: "grid_item",
      fieldName: "carat_chest_tightness",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.8",
      number: "21.8",
      text: "Tiredness/ limitations in doing daily tasks because of your allergic respiratory diseases?",
      type: "grid_item",
      fieldName: "carat_tiredness",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q21.9",
      number: "21.9",
      text: "Woke up during the night because of your allergic respiratory diseases?",
      type: "grid_item",
      fieldName: "carat_night_waking",
      required: true,
      parentGrid: "q21.0",
      options: [
        { value: "A", label: "Never", numericValue: 3 },
        { value: "B", label: "1 or 2 days per week", numericValue: 2 },
        { value: "C", label: "More than 2 days per week", numericValue: 1 },
        { value: "D", label: "Almost every day", numericValue: 0 }
      ],
      automation: null
    },

    // Q22 - Additional CARAT question
    {
      id: "q22",
      number: 22,
      text: "In the last 4 weeks, because of your allergic respiratory diseases (asthma, rhinits, allergies), how many times did you (or your child): Increase the use (dosage or frequency) of your medicines?",
      type: "radio",
      fieldName: "medicine_increase",
      required: true,
      options: [
        { value: "A", label: "I am not taking any medicines", numericValue: 3 },
        { value: "B", label: "Never", numericValue: 2 },
        { value: "C", label: "Less than 7 days", numericValue: 1 },
        { value: "D", label: "7 or more days", numericValue: 0 }
      ],
      automation: null
    },

    // Skin Allergy Symptoms Group
    {
      id: "q23",
      number: 23,
      text: "Skin Allergy Symptoms",
      type: "group",
      fieldName: "skin_symptoms_group",
      required: true,
      sectionText: "Did you know? Baby skin is ~30% thinner than adult skin, so it's extra sensitive.",
      estimatedTime: "30 seconds",
      automation: null
    },

    {
      id: "q24",
      number: 24,
      text: "In the past few months, have you (or your child) had any of these skin symptoms?",
      type: "checkbox",
      fieldName: "skin_symptoms",
      required: false,
      subText: "If you do not have any of these symptoms go to the next question.",
      options: [
        { value: "A", label: "Rash", numericValue: 1 },
        { value: "B", label: "Hives", numericValue: 2 },
        { value: "C", label: "Eczema", numericValue: 3 },
        { value: "D", label: "Swelling", numericValue: 4 },
        { value: "E", label: "Itchy skin", numericValue: 5 }
      ],
      automation: {
        type: "conditional_display",
        rules: [
          {
            condition: "[24] = A OR B OR C OR D OR E",
            action: "enable",
            targetQuestions: ["q25", "q26", "q27"],
            description: "Enable skin symptom timing questions if any skin symptoms selected"
          }
        ]
      }
    },

    {
      id: "q25",
      number: 25,
      text: "Do you have these skin symptoms:",
      type: "checkbox",
      fieldName: "skin_symptoms_timing",
      required: false,
      showIf: "[24] = A OR B OR C OR D OR E",
      options: [
        { value: "A", label: "All year round", numericValue: 1 },
        { value: "B", label: "Spring", numericValue: 2 },
        { value: "C", label: "Summer", numericValue: 3 },
        { value: "D", label: "Fall", numericValue: 4 },
        { value: "E", label: "Winter", numericValue: 5 },
        { value: "F", label: "They just started", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q26",
      number: 26,
      text: "Are your skin symptoms worse:",
      type: "checkbox",
      fieldName: "skin_symptoms_worse",
      required: false,
      showIf: "[24] = A OR B OR C OR D OR E",
      options: [
        { value: "A", label: "At night", numericValue: 1 },
        { value: "B", label: "During the day", numericValue: 2 },
        { value: "C", label: "Both night and day", numericValue: 3 }
      ],
      automation: null
    },

    {
      id: "q27",
      number: 27,
      text: "Do your skin symptoms mostly occur:",
      type: "checkbox",
      fieldName: "skin_symptoms_location",
      required: false,
      showIf: "[24] = A OR B OR C OR D OR E",
      options: [
        { value: "A", label: "Indoor", numericValue: 1 },
        { value: "B", label: "Outdoor", numericValue: 2 },
        { value: "C", label: "Both indoor and outdoor", numericValue: 3 }
      ],
      automation: null
    },

    // Medication Use Group
    {
      id: "q28",
      number: 28,
      text: "Medication Use",
      type: "group",
      fieldName: "medication_use_group",
      required: true,
      showIf: "([7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J) OR ([11] = A OR B OR C OR D OR E OR F) OR ([17] = A OR B OR C OR D OR E ) OR [15] = A",
      sectionText: "Almost done [FirstName] — your input ensures treatments stay safe and effective.",
      estimatedTime: "<1 min",
      automation: null
    },

    // Grid for medication questions
    {
      id: "q28_grid",
      number: 28,
      text: "In the past two weeks, how often did you (or your child) use any of these medicines for allergy symptoms?",
      type: "grid",
      fieldName: "medication_use_grid",
      required: true,
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    // Individual medication questions
    {
      id: "q28.1",
      number: "28.1",
      text: "Oral (tablets or liquids) antihistamines - For example, Benadryl, Alavert, Allegra, Claritin, Tavist, Zyrtec, Xyzal or pharmacy branded allergy tablets or liquids.",
      type: "grid_item",
      fieldName: "med_oral_antihistamines",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.2",
      number: "28.2",
      text: "Antihistamine nasal sprays - For Example, Astopro, or Patanase or pharmacy branded sprays.",
      type: "grid_item",
      fieldName: "med_nasal_antihistamines",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.3",
      number: "28.3",
      text: "Eye drop antihistamines - For example Bepreve, Opcon-A, Pataday or other.",
      type: "grid_item",
      fieldName: "med_eye_antihistamines",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.4",
      number: "28.4",
      text: "Oral decongestants - For example, Afrin, Sudafed, Vicks Sinex, Suphedrine PE, Silfedrine other pseudoephedrine products.",
      type: "grid_item",
      fieldName: "med_oral_decongestants",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.5",
      number: "28.5",
      text: "Decongestant nasal sprays - For example, Afrin, Allerest, Mucinex Sinus, Zicam, Vicks Sinex.",
      type: "grid_item",
      fieldName: "med_nasal_decongestants",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.6",
      number: "28.6",
      text: "Nasal steroid sprays - For example, Flonase, Beconase AQ, Qnasl, Rhinocort, Vermamyst, Nasonex, Nasacort.",
      type: "grid_item",
      fieldName: "med_nasal_steroids",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    {
      id: "q28.7",
      number: "28.7",
      text: "Singulair (Montelukast)",
      type: "grid_item",
      fieldName: "med_singulair",
      required: true,
      parentGrid: "q28_grid",
      options: [
        { value: "A", label: "Daily", numericValue: 1 },
        { value: "B", label: "More than once a week", numericValue: 2 },
        { value: "C", label: "Weekly", numericValue: 3 },
        { value: "D", label: "Occasionally", numericValue: 4 },
        { value: "E", label: "Not at all", numericValue: 5 },
        { value: "F", label: "Unsure", numericValue: 6 }
      ],
      automation: null
    },

    // Help-Seeking Behavior Group
    {
      id: "q29",
      number: 29,
      text: "Help-Seeking Behavior",
      type: "group",
      fieldName: "help_seeking_group",
      required: true,
      sectionText: "Thank you [FirstName] — these last few questions help us guide next steps and you'll receive your customized feedback report.",
      estimatedTime: "30 seconds",
      automation: null
    },

    {
      id: "q29.1",
      number: "29.1",
      text: "Would you (or your child) like help with emotional well-being, including stress or worry?",
      type: "radio",
      fieldName: "wants_emotional_help",
      required: true,
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No, not right now.", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q29.2",
      number: "29.2",
      text: "It looks like there may be allergy problems. Would you (or your child) like an allergy test to help find out what is causing them?",
      type: "radio",
      fieldName: "wants_allergy_test",
      required: true,
      showIf: "([7] = A OR B OR C OR D OR E OR F OR G OR H OR I OR J) OR ([11] = A OR B OR C OR D OR E OR F) OR ([17] = A OR B OR C OR D OR E ) OR ([24] = A OR B OR C OR D OR E )",
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No, not right now.", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q29.3",
      number: "29.3",
      text: "Based on your responses today, it appears you are having allergy troubles and OTC medications are not controlling your symptoms, are you interested in alternative allergy treatment to treat the cause of your allergies?",
      type: "radio",
      fieldName: "wants_alternative_treatment",
      required: true,
      showIf: "SUM [21.1,21.2, 21.3,21.4,21.5, 21.6,21.7,21.8,21.9,22] =  <=19 AND ([28.1] A OR B OR C) OR ([28.6] A OR B OR C)",
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No, not right now.", numericValue: 0 }
      ],
      automation: null
    },

    {
      id: "q29.4",
      number: "29.4",
      text: "Would you (or your child) like help with any of the following; relaxation, weight loss, healthy eating, or exercise.",
      type: "radio",
      fieldName: "wants_lifestyle_help",
      required: true,
      options: [
        { value: "A", label: "Yes", numericValue: 1 },
        { value: "B", label: "No, not right now.", numericValue: 0 }
      ],
      automation: null
    }
  ]
};